import { supabase } from './supabase';
import type {
  BehandelingCatalog,
  BehandelingCatalogItem,
  BehandelingResult,
  KleedBehandelingBron,
} from './kleed-behandeling';

export type {
  BehandelingCatalog,
  BehandelingCatalogItem,
  BehandelingResult,
  KleedBehandelingBron,
};

async function authHeaders(): Promise<Record<string, string>> {
  const { data: { session } } = await supabase.auth.getSession();
  const h: Record<string, string> = { 'Content-Type': 'application/json' };
  if (session?.access_token) h['Authorization'] = `Bearer ${session.access_token}`;
  return h;
}

export async function fetchWasserijItemBehandelingCatalog(
  parentItemId: string
): Promise<BehandelingCatalog> {
  const headers = await authHeaders();
  const qp = new URLSearchParams({ parentItemId });
  const response = await fetch(`/api/orders/wasserij-item-behandeling?${qp.toString()}`, { headers });
  const data = await response.json().catch(() => ({}));
  // Een 500 komt als { error, detail } zonder `message`; anders blijft de
  // dialog leeg zonder uitleg (11-09-2026, dode SF-catalogus).
  return {
    success: data.success === true,
    message: data.message || (response.ok ? undefined : data.error || `Catalogus laden mislukt (${response.status})`),
    errorCode: data.errorCode,
    parentName: data.parentName,
    parentTotaalInclVat: data.parentTotaalInclVat,
    items: Array.isArray(data.items) ? (data.items as BehandelingCatalogItem[]) : [],
  };
}

export interface AddWasserijItemBehandelingArgs {
  parentItemId: string;
  behandelingCode: string;
  bron?: KleedBehandelingBron;
}

export async function addWasserijItemBehandeling(
  args: AddWasserijItemBehandelingArgs
): Promise<BehandelingResult> {
  const headers = await authHeaders();
  const body = {
    parentItemId: args.parentItemId,
    behandelingCode: args.behandelingCode,
    bron: args.bron || 'Office-App',
  };
  const response = await fetch('/api/orders/wasserij-item-behandeling', {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  const data = await response.json().catch(() => ({} as BehandelingResult));
  if (!response.ok && data.success !== true) {
    return {
      success: false,
      message: data.message || `Behandeling toevoegen mislukt (${response.status})`,
      errorCode: data.errorCode,
    };
  }
  return {
    success: data.success === true || (data as { ok?: boolean }).ok === true,
    message: data.message,
    errorCode: data.errorCode,
    childId: data.childId,
  };
}

export async function removeWasserijItemBehandeling(
  childId: string
): Promise<BehandelingResult> {
  const headers = await authHeaders();
  const qp = new URLSearchParams({ childId });
  const response = await fetch(`/api/orders/wasserij-item-behandeling?${qp.toString()}`, {
    method: 'DELETE',
    headers,
  });
  const data = await response.json().catch(() => ({} as BehandelingResult));
  if (!response.ok && data.success !== true) {
    return {
      success: false,
      message: data.message || `Behandeling verwijderen mislukt (${response.status})`,
      errorCode: data.errorCode,
    };
  }
  return {
    success: data.success === true || (data as { ok?: boolean }).ok === true,
    message: data.message,
    errorCode: data.errorCode,
  };
}
